import { Link, useParams, Navigate } from "react-router-dom";
import Logo from "../components/Logo";
import { getPostBySlug, POSTS } from "../data/blogPosts";
import { ArrowLeft, ArrowRight, Clock, Sparkle, Star } from "@phosphor-icons/react";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen relative noise-bg" data-testid="blog-post-page">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0A0A0A]/85 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/blog" className="text-xs text-neutral-500 hover:text-white transition-colors inline-flex items-center gap-1">
            <ArrowLeft size={12} /> All posts
          </Link>
        </div>
      </header>

      <article className="relative z-10 max-w-3xl mx-auto px-4 sm:px-8 pt-10 sm:pt-16 pb-16">
        {/* Header */}
        <div className="mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 pill pill-primary mb-5">
            {post.tag}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1]" data-testid="blog-post-title">
            {post.title}
          </h1>
          <p className="text-neutral-400 mt-5 text-base sm:text-lg leading-relaxed">{post.excerpt}</p>
          <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF5A1F] to-[#141414] flex items-center justify-center shrink-0">
                <span className="font-display text-xs font-semibold text-white">
                  {post.author.split(" ").map(s => s[0]).join("").toUpperCase()}
                </span>
              </div>
              <div>
                <div className="text-sm font-semibold text-white">{post.author}</div>
                <div className="text-xs text-neutral-500">{post.author_title}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-neutral-500 uppercase tracking-widest">
              <span>{new Date(post.published_at).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}</span>
              <span className="flex items-center gap-1">
                <Clock size={11} /> {post.read_min} min read
              </span>
            </div>
          </div>
        </div>

        {/* Cover */}
        <div className={`aspect-[16/9] rounded-2xl bg-gradient-to-br ${post.cover_gradient} border border-white/10 mb-10 sm:mb-12 relative overflow-hidden`}>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-6">
              <div className="text-xs uppercase tracking-widest text-white/70 mb-2">{post.tag}</div>
              <div className="font-display text-2xl sm:text-3xl font-semibold text-white max-w-md">{post.title}</div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="space-y-6 text-neutral-200 text-base sm:text-[17px] leading-[1.75]" data-testid="blog-post-content">
          {post.content.map((block, i) => <Block key={i} block={block} />)}
        </div>

        {/* Author card */}
        <div className="mt-16 pt-8 border-t border-white/10 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF5A1F] to-[#141414] flex items-center justify-center shrink-0">
            <span className="font-display text-sm font-semibold text-white">
              {post.author.split(" ").map(s => s[0]).join("").toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">Written by {post.author}</div>
            <div className="text-xs text-neutral-500">{post.author_title} · Book a call with him below</div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-8 card-surface p-6 sm:p-8">
          <div className="flex items-center gap-0.5 mb-3 text-[#FF5A1F]">
            {[0, 1, 2, 3, 4].map(i => <Star key={i} size={12} weight="fill" />)}
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">
            Want to see this in action?
          </h3>
          <p className="text-neutral-400 mt-2 text-sm leading-relaxed">
            Book a 30-min screen-share and I'll show you AI drafting messages on your actual loads, live.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <Link
              to="/demo"
              data-testid="blog-post-book-demo"
              className="btn-primary rounded-full px-5 py-3 text-sm inline-flex items-center justify-center gap-2"
            >
              Book a demo <ArrowRight size={13} weight="bold" />
            </Link>
            <Link
              to="/signup"
              className="rounded-full px-5 py-3 text-sm border border-white/15 hover:border-white/30 text-white text-center transition-colors"
            >
              Or try free for 7 days
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-12 border-t border-white/10">
          <div className="overline mb-4">MORE FROM THE BLOG</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="card-surface p-5 hover-lift group"
                data-testid={`blog-related-${p.slug}`}
              >
                <div className="overline mb-2">{p.tag}</div>
                <h4 className="font-display text-lg font-semibold text-white group-hover:text-[#FF5A1F] transition-colors">{p.title}</h4>
                <p className="text-sm text-neutral-400 mt-2 line-clamp-2">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="relative z-10 border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} Freight Pilot AI</div>
          <div className="flex gap-6">
            <Link to="/services" className="hover:text-white transition-colors">Services</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-10 mb-2 text-white">{block.text}</h2>;
    case "p":
      return <p className="text-neutral-200">{block.text}</p>;
    case "quote":
      return (
        <blockquote className="my-6 pl-6 border-l-2 border-[#FF5A1F] text-neutral-100 italic text-lg leading-relaxed">
          "{block.text}"
        </blockquote>
      );
    case "list":
      return (
        <ul className="space-y-2 my-4">
          {block.items.map((it, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] mt-2.5 shrink-0" />
              <span className="text-neutral-200">{it}</span>
            </li>
          ))}
        </ul>
      );
    case "cta":
      return (
        <div className="my-8">
          <Link
            to={block.href || "/"}
            className="inline-flex items-center gap-2 text-[#FF5A1F] hover:text-[#FF7038] font-semibold border-b border-[#FF5A1F]/40 hover:border-[#FF7038] pb-0.5"
          >
            <Sparkle size={14} weight="fill" /> {block.text}
          </Link>
        </div>
      );
    default:
      return null;
  }
}
